import { useTheme } from '@material-ui/core';
import AsyncCalendar from 'components/AsyncCalendar/AsyncCalendar';
import { format } from 'date-fns';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useSetHeaderTitle } from 'shared/hooks/useSetHeader';
import { ITalentCalendarEventAttributes } from 'shared/interfaces/ITalentCalendar';
import { TALENT_CALENDAR_EVENTS, TALENT_SEARCH_CALENDAR_EVENTS } from 'shared/queries/talentCalendar.queries';
import { castingService } from 'shared/services/castingService';
import { talentService } from 'shared/services/talentCalendarService';
import { ls } from 'shared/utils/ls';
import { useDebounce } from 'use-debounce';

const {
  createCalendarEvent,
  updateCalendarEvent,
  deleteCalendarEvent,
  getCalendarAggregates,
  getTalentCalendarEvents,
} = talentService();

const Event = () => {
  useSetHeaderTitle(`Calendar`);
  const { getLS } = ls();
  const { id: talentId } = JSON.parse(getLS('user')) || {};
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [searchString, setSearchString] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [debouncedSearchQuery] = useDebounce(searchString, 500);
  const [currentView, setCurrentView] = useState<'day' | 'week' | 'month' | 'year'>('day');
  const { mutateAsync: createEvent } = createCalendarEvent();
  const { mutateAsync: updateEvent } = updateCalendarEvent();
  const { mutateAsync: deleteEvent } = deleteCalendarEvent();
  const userTimezone = moment.tz.guess();

  const handleSearch = (search: string) => {
    setSearchString(search);
  };

  const handleCreateEvent = async (payload: any, resetForm: any) => {
    await createEvent(
      {
        ...payload,
        talent_id: talentId,
      },
      {
        onSuccess: async (_: any) => {
          resetForm();
          queryClient.invalidateQueries('talent_calendar_events');
          queryClient.invalidateQueries('calendar_aggregate');
        },
      },
    );
  };

  const handleUpdateEvent = async (payload: any, resetForm: any) => {
    await updateEvent(
      {
        ...payload,
        talent_id: talentId,
      },
      {
        onSuccess: async (e: any) => {
          resetForm();
          await queryClient.removeQueries('talent_calendar_events');
          await queryClient.invalidateQueries('calendar_aggregate');
        },
      },
    );
  };

  const handleDeleteEvent = async (id: string, talentId: string) => {
    await deleteEvent(
      { id, talentId },
      {
        onSuccess: async (data, variables) => {
          await queryClient.removeQueries('talent_calendar_events');
          await queryClient.invalidateQueries('calendar_aggregate');
        },
      },
    );
  };

  const { data: aggregates, isLoading: isAggregatesLoading } = getCalendarAggregates(
    {
      date_from:
        currentView === 'year'
          ? moment(selectedDate).startOf('year').format('YYYY-MM-DD')
          : moment(selectedDate).startOf('month').format('YYYY-MM-DD'),
      date_to:
        currentView === 'year'
          ? moment(selectedDate).endOf('year').format('YYYY-MM-DD')
          : moment(selectedDate).endOf('month').format('YYYY-MM-DD'),
      ...{ timezone: userTimezone },
      ...{ talent_id: talentId },
      ...(selectedEvents.length > 0 && { calendar_event_ids: selectedEvents }),
    },
    {
      enabled: !!talentId,
    },
  );

  const { data, isLoading, isRefetching, fetchNextPage, hasNextPage } = getTalentCalendarEvents(
    {
      first: 10,
      after: '',
      date_from:
        currentView === 'day'
          ? moment(selectedDate).format('YYYY-MM-DD')
          : currentView === 'week'
            ? moment(selectedDate).startOf('isoWeek').format('YYYY-MM-DD')
            : currentView === 'month'
              ? moment(selectedDate).startOf('month').subtract(6, 'days').format('YYYY-MM-DD')
              : currentView === 'year'
                ? moment(selectedDate).startOf('year').format('YYYY-MM-DD')
                : '',
      date_to:
        currentView === 'day'
          ? moment(selectedDate).format('YYYY-MM-DD')
          : currentView === 'week'
            ? moment(selectedDate).endOf('isoWeek').format('YYYY-MM-DD')
            : currentView === 'month'
              ? moment(selectedDate).endOf('month').add(6, 'days').format('YYYY-MM-DD')
              : currentView === 'year'
                ? moment(selectedDate).endOf('year').format('YYYY-MM-DD')
                : '',
      ...{ timezone: userTimezone },
      ...{ talent_ids: [talentId] },
      ...(selectedEvents.length > 0 && { calendar_event_ids: selectedEvents }),
    },
    TALENT_CALENDAR_EVENTS,
    {
      enabled: !!talentId && currentView !== 'year',
    },
  );

  const {
    data: searchedData,
    isLoading: isSearchLoading,
    isRefetching: isSearchRefetching,
    fetchNextPage: fetchNextSearchPage,
    hasNextPage: hasSearchNextPage,
  } = getTalentCalendarEvents(
    {
      first: 10,
      after: '',
      search: debouncedSearchQuery?.trim(),
      // date_from: moment(selectedDate).subtract(1, 'year').format('YYYY-MM-DD'),
      // date_to: moment(selectedDate).add(1, 'year').format('YYYY-MM-DD'),
      ...{ timezone: userTimezone },
      ...{ talent_ids: [talentId] },
    },
    TALENT_SEARCH_CALENDAR_EVENTS,
    {
      enabled: !!talentId && isSearchOpen,
    },
  );

  const searchedEvent = useMemo(() => {
    return searchedData
      ? searchedData.pages.reduce((acc: ITalentCalendarEventAttributes[], curr) => {
          if (curr.data.calendar_events_connection.nodes) {
            return [...acc, ...curr.data.calendar_events_connection.nodes];
          }
          return acc;
        }, [])
      : [];
  }, [searchedData]);

  const calendarEvents = useMemo(() => {
    return data
      ? data.pages.reduce((acc: ITalentCalendarEventAttributes[], curr) => {
          if (curr.data.calendar_events_connection.nodes) {
            return [...acc, ...curr.data.calendar_events_connection.nodes];
          }
          return acc;
        }, [])
      : [];
  }, [data]);

  return (
    <div>
      <AsyncCalendar
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        handleSearch={handleSearch}
        handleCreateEvent={handleCreateEvent}
        handleUpdateEvent={handleUpdateEvent}
        handleDeleteEvent={handleDeleteEvent}
        currentView={currentView}
        setCurrentView={setCurrentView}
        talentIds={[talentId]}
        aggregates={aggregates}
        isAggregatesLoading={isAggregatesLoading}
        events={calendarEvents}
        isLoading={isLoading}
        isRefetching={isRefetching}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        allowedTypes={['personal']}
        isSearchLoading={isSearchLoading}
        isSearchRefetching={isSearchRefetching}
        fetchNextSearchPage={fetchNextSearchPage}
        hasSearchNextPage={hasSearchNextPage}
        searchedEvent={searchedEvent || []}
        selectedEvents={selectedEvents}
        setSelectedEvents={setSelectedEvents}
        isSearchOpen={isSearchOpen}
        setIsSearchOpen={setIsSearchOpen}
        theme={theme.palette.type}
        company={undefined}
        castingService={castingService}
        timezone={userTimezone}
        enableToggleEditDelete={true}
      />
    </div>
  );
};

export default Event;
